import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Trophy, Clock } from 'lucide-react';

export interface PrizeEntry {
  position: string;
  prize: string;
}

export interface PrizeTab {
  label: string;
  prizes: PrizeEntry[];
  bonus?: string;
  start_date?: string;
  end_date?: string;
  timezone?: string;
}

export interface PrizeDataStructure {
  tabs: PrizeTab[];
  footer?: string;
}

export function validatePrizeData(data: unknown): data is PrizeDataStructure {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  if (!Array.isArray(d.tabs)) return false;
  for (const tab of d.tabs as unknown[]) {
    if (!tab || typeof tab !== 'object') return false;
    const t = tab as Record<string, unknown>;
    if (typeof t.label !== 'string') return false;
    if (!Array.isArray(t.prizes)) return false;
    for (const p of t.prizes as unknown[]) {
      if (!p || typeof p !== 'object') return false;
      const prize = p as Record<string, unknown>;
      if (typeof prize.position !== 'string') return false;
      if (typeof prize.prize !== 'string') return false;
    }
    if (t.bonus !== undefined && typeof t.bonus !== 'string') return false;
    if (t.timezone !== undefined && typeof t.timezone !== 'string') return false;
  }
  if (d.footer !== undefined && typeof d.footer !== 'string') return false;
  return true;
}

export function parsePrizeJson(json: string): PrizeDataStructure | null {
  try {
    const parsed = JSON.parse(json);
    return validatePrizeData(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Convert a stored wall-clock datetime (naive UTC) to the actual UTC instant
 * for a given timezone. Stored datetimes represent wall-clock times in the
 * ranking's timezone, so we need to adjust for the timezone offset.
 */
function wallClockToUTC(dateStr: string, timezone: string): Date {
  // Normalize: prize tab dates lack the Z suffix (e.g. "2026-03-14T00:00")
  // while ranking dates have it ("2026-03-14T00:00:00.000Z"). Without Z,
  // new Date() treats the string as browser-local time, breaking the offset
  // calculation. Force naive-UTC interpretation by ensuring it ends with Z.
  let normalized = dateStr;
  if (!normalized.endsWith('Z')) {
    // "2026-03-14T00:00" → add seconds + Z
    if (normalized.length === 16) normalized += ':00.000Z';
    // "2026-03-14T00:00:00" → add Z
    else normalized += 'Z';
  }
  const d = new Date(normalized);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value;
  const localStr = `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}.000Z`;
  const localAsUTC = new Date(localStr);
  const offsetMs = localAsUTC.getTime() - d.getTime();
  return new Date(d.getTime() - offsetMs);
}

function secondsUntil(dateTime: string, timezone?: string): number {
  const target = wallClockToUTC(dateTime, timezone || 'America/Sao_Paulo');
  return Math.floor((target.getTime() - Date.now()) / 1000);
}

function formatCountdown(seconds: number): string {
  const abs = Math.abs(seconds);
  const d = Math.floor(abs / 86400);
  const h = Math.floor((abs % 86400) / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const s = abs % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function formatPrize(value: string): string {
  const trimmed = value.trim();
  const num = Number(trimmed);
  if (trimmed !== '' && !isNaN(num)) {
    return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }
  return value;
}

const MEDALS = ['🥇', '🥈', '🥉'];

interface PrizePoolProps {
  data: PrizeDataStructure;
  title: string;
  subtitle: string;
}

export const PrizePool = ({ data, title, subtitle }: PrizePoolProps) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [, setTick] = useState(0);

  const hasEndDate = data.tabs.some(t => t.end_date);

  useEffect(() => {
    if (!hasEndDate) return;
    const id = setInterval(() => setTick(n => n + 1), 1000);
    return () => clearInterval(id);
  }, [hasEndDate]);

  if (!data.tabs || data.tabs.length === 0) return null;

  const safeIndex = activeTab < data.tabs.length ? activeTab : 0;
  const tab = data.tabs[safeIndex];

  return (
    <Card className="bg-card border-border">
      <CardContent className="p-5">
        {/* Header row */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <Trophy className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="font-bold text-lg">{title}</h3>
              <p className="text-sm text-muted-foreground">"{subtitle}"</p>
            </div>
          </div>

          {data.tabs.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {data.tabs.map((tab, i) => {
                const secs = tab.end_date ? secondsUntil(tab.end_date, tab.timezone) : null;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setActiveTab(i)}
                    className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors flex flex-col items-center gap-0.5 ${
                      i === safeIndex
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <span>{tab.label || `Tab ${i + 1}`}</span>
                    {secs !== null && (
                      <span className={`text-[10px] font-normal flex items-center gap-0.5 ${
                        i === safeIndex ? 'opacity-80' : 'opacity-60'
                      }`}>
                        <Clock className="w-2.5 h-2.5" />
                        {secs > 0 ? formatCountdown(secs) : t('challenge.rankings.consolidating')}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Prize cards */}
        {tab.prizes.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            {tab.prizes.map((p, i) => (
              <div key={i} className="bg-background rounded-xl p-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                  {MEDALS[i] ? <span className="mr-1">{MEDALS[i]}</span> : null}
                  {p.position}
                </div>
                <div className="text-2xl font-bold text-primary">{formatPrize(p.prize)}</div>
              </div>
            ))}
          </div>
        )}

        {/* Bonus row */}
        {tab.bonus && (
          <div className="bg-primary/5 border border-primary/20 rounded-xl px-4 py-3 flex items-center gap-2 mb-3">
            <span className="text-lg">🎁</span>
            <span className="font-semibold text-primary text-sm">{tab.bonus}</span>
          </div>
        )}

        {/* Countdown row for active tab */}
        {tab.end_date && (() => {
          const secs = secondsUntil(tab.end_date, tab.timezone);
          return (
            <div className="flex items-center gap-2 text-xs text-muted-foreground border-t border-border/40 pt-3 mt-1">
              <Clock className="w-3.5 h-3.5 shrink-0 text-primary/70" />
              {secs > 0
                ? <><span className="text-foreground/70">{t('challenge.rankings.timeRemaining')}:</span><span className="font-semibold tabular-nums text-primary">{formatCountdown(secs)}</span></>
                : <span className="text-amber-400">{t('challenge.rankings.consolidating')}</span>
              }
            </div>
          );
        })()}

        {/* Footer note */}
        {data.footer && (
          <p className="text-xs text-muted-foreground italic mt-3">"{data.footer}"</p>
        )}
      </CardContent>
    </Card>
  );
};
